import { Component, inject, OnInit } from '@angular/core';
import { BaseSubscription } from '@mogenius/core/components/base-subscription';
import { ConfirmAppConnectionResponseDto, GithubInstallationDto } from '@mogenius/client-sdk/core-dto';
import { ActivatedRoute, Router } from '@angular/router';
import { GitAuthorizedEnum } from '@mogenius/git/enums/git-authorized.enum';
import { WorkspaceGitConnectionGithubService } from '@mogenius/workspace/services/git-integration/git-provider/workspace-git-connection-github.service';
import { MoUtils } from '@mogenius/client-sdk/utils';

import { NgOptimizedImage } from '@angular/common';
import { SpinnerComponent } from '@mogenius/shared/components/spinner/spinner.component';

@Component({
  selector: 'mo-workspace-github-authorized-page',
  templateUrl: './workspace-github-authorized-page.component.html',
  styleUrl: './workspace-github-authorized-page.component.scss',
  imports: [NgOptimizedImage, SpinnerComponent]
})
export class WorkspaceGithubAuthorizedPageComponent extends BaseSubscription implements OnInit {
  private readonly _router: Router = inject(Router);
  private readonly _activatedRoute: ActivatedRoute = inject(ActivatedRoute);
  private readonly _workspaceGitConnectionGithubService: WorkspaceGitConnectionGithubService = inject(
    WorkspaceGitConnectionGithubService
  );

  private _state: GitAuthorizedEnum = GitAuthorizedEnum.LOADING;
  private _data: ConfirmAppConnectionResponseDto;
  private _message: string;
  private _fallbackUrl: string | null = localStorage.getItem('gitConnectionFallbackUrl');

  ngOnInit(): void {
    const params = this._activatedRoute.snapshot.queryParams;

    // TODO remove after migration
    if (params.state && params.state.endsWith(':workspace')) {
      params.state = MoUtils.replaceAll(params.state, ':workspace', '');
    }

    this._router
      .navigate([], {
        queryParams: {
          state: null,
          code: null,
          installation_id: null
        },
        queryParamsHandling: 'merge'
      })
      .then(
        () => {},
        () => {}
      );

    this._subscriptions.add(
      this._workspaceGitConnectionGithubService.confirmAppConnection(params).subscribe({
        next: (data) => {
          this._data = data;
          if (this._data.type === 'SELECT_INSTALLATION') {
            this._state = GitAuthorizedEnum.SUCCESS;
            return;
          } else if (this._data.type === 'INSTALLATION') {
            window.open(this._data.callbackUrl, '_self');
            return;
          } else if (this._data.type === 'TO_GITHUB') {
            this.openGithubNewInstallation();
            return;
          } else if (this._data.type === 'AUTHORIZED') {
            window.open(this._fallbackUrl ?? this._data.callbackUrl, '_self');
          }
        },
        error: (err) => {
          this._state = GitAuthorizedEnum.FAILED;
          console.log(err);
          this._message = err?.error?.message;
        }
      })
    );
  }

  public selectInstallation(installation: GithubInstallationDto): void {
    this._subscriptions.add(
      this._workspaceGitConnectionGithubService
        .selectInstallationAsConnection({
          installationId: installation.id,
          installationsKey: this._data.installationsKey,

          organizationId: this._data.organizationId,
          clusterId: this._data.clusterId,
          workspaceName: this._data.workspaceName
        })
        .subscribe({
          next: () => {
            if (this._data.callbackUrl) {
              window.open(this._data.callbackUrl ?? this._fallbackUrl, '_self');
            }
          },
          error: (err) => {},
          complete: () => {}
        })
    );
  }

  public openGithubNewInstallation(): void {
    this._subscriptions.add(
      this._workspaceGitConnectionGithubService
        .requestAppConnectionLink({
          organizationId: this._data.organizationId,
          clusterId: this._data.clusterId,
          workspaceName: this._data.workspaceName,
          callbackUrl: undefined
        })
        .subscribe({
          next: (link: string) => {
            window.open(link, '_self');
          },
          error: () => {},
          complete: () => {}
        })
    );
  }

  get gitAuthorizedEnum(): typeof GitAuthorizedEnum {
    return GitAuthorizedEnum;
  }

  get state(): GitAuthorizedEnum {
    return this._state;
  }

  get data(): ConfirmAppConnectionResponseDto {
    return this._data;
  }

  get message(): string {
    return this._message;
  }

  get fallbackUrl(): string | null {
    return this._fallbackUrl;
  }
}
